/**
 * Herstel na een deploy: de pagina's laden lazy (App.jsx). Na een nieuwe deploy bestaan
 * de oude chunk-bestanden niet meer, dus een gebruiker die de app nog open had krijgt bij
 * het openen van een pagina een fout. Dan laden we de pagina één keer opnieuw, zodat de
 * nieuwe versie (met de nieuwe chunk-namen) binnenkomt.
 *
 * Tegen een reload-lus: in sessionStorage staat wanneer we voor het laatst automatisch
 * herladen hebben. Is dat korter dan RELOAD_WINDOW_MS geleden, dan herladen we niet nog
 * eens en toont de ErrorBoundary een melding met een knop "Pagina herladen".
 */

const RELOAD_FLAG_KEY = 'bem:chunk-reload-at';
// Ruim genoeg voor een trage pagina-load, kort genoeg om bij een volgende deploy weer
// automatisch te mogen herladen
const RELOAD_WINDOW_MS = 30 * 1000;

// Of er in deze pagina al een automatische reload loopt
let reloading = false;

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

/** Of `error` komt doordat een (lazy) chunk niet geladen kon worden. */
export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_PATTERN.test(String(error.message ?? error));
}

/**
 * Herlaadt de pagina, maar alleen als dat niet net al gebeurd is.
 * @returns {boolean} true als er herladen wordt; false als het al geprobeerd is (of
 *   sessionStorage niet beschikbaar is), zodat de aanroeper een melding moet tonen.
 */
export function reloadOnceForChunkError() {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG_KEY));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // Zonder sessionStorage kunnen we een lus niet uitsluiten: niet automatisch herladen
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

/**
 * Of de pagina nu herlaadt. Na preventDefault() levert Vite's import `undefined` op,
 * waardoor React.lazy nog een (andere) fout gooit vóór de reload: die hoeft de
 * ErrorBoundary niet als crash te tonen.
 */
export function isReloadingForChunkError() {
  return reloading;
}

/**
 * Vite meldt een mislukte dynamische import (of preload) via het event `vite:preloadError`.
 * Ook fouten buiten de React-render (bv. een `import()` in een klikhandler) komen zo langs.
 * Met preventDefault() gooit Vite de fout niet door; dat doen we alleen als we herladen.
 * Anders gaat de fout gewoon door naar de ErrorBoundary, die dan de melding toont.
 */
export function installChunkErrorHandler() {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForChunkError()) event.preventDefault();
  });
}
